import { useState, useMemo } from "react";
import { ChevronsUpDown, Search, X, Building2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getRecentCustomerIds, pushRecentCustomerId } from "./customer-combobox/recentCustomers";
import { CustomerItem } from "./customer-combobox/CustomerItem";
import { useCustomerSearch } from "./customer-combobox/useCustomerSearch";

export function CustomerCombobox({
  customers = [],
  value,
  onChange,
  placeholder = "Kunde auswählen...",
  allowClear = true,
  testid = "customer-combobox"
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [recentIds, setRecentIds] = useState(() => getRecentCustomerIds());
  const customerList = useMemo(() => (Array.isArray(customers) ? customers : []), [customers]);

  const selected = useMemo(
    () => customerList.find((c) => c.id === value) || null,
    [customerList, value]
  );

  const { recentCustomers, filteredCustomers, groupedCustomers } = useCustomerSearch(
    customerList,
    search,
    recentIds
  );

  const handleSelect = (customerId) => {
    const newValue = customerId === value ? "" : customerId;
    if (newValue) {
      pushRecentCustomerId(newValue);
      setRecentIds(getRecentCustomerIds());
    }
    onChange(newValue);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-11 md:h-10 font-normal",
            !selected && "text-muted-foreground"
          )}
          data-testid={testid}
        >
          {selected ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Building2 className="w-4 h-4 shrink-0 text-primary" />
              <span className="truncate">{selected.company_name}</span>
              {selected.city && (
                <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">
                  · {selected.city}
                </span>
              )}
            </div>
          ) : (
            <span className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              {placeholder}
            </span>
          )}
          <div className="flex items-center gap-1 shrink-0">
            {selected && allowClear && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleClear(e);
                }}
                className="p-1 hover:bg-muted rounded cursor-pointer"
                data-testid={`${testid}-clear`}
                aria-label="Auswahl löschen"
              >
                <X className="w-3.5 h-3.5 opacity-60 hover:opacity-100" />
              </span>
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Suche nach Name, Ort oder PLZ..."
            className="h-11"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-[320px]">
            {filteredCustomers.length === 0 && (
              <CommandEmpty>
                <div className="py-6 text-center text-sm">
                  <Search className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-muted-foreground">Keine Kunden gefunden</p>
                </div>
              </CommandEmpty>
            )}
            {recentCustomers.length > 0 && (
              <CommandGroup
                heading={
                  <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold">
                    <Clock className="w-3 h-3" />
                    Zuletzt verwendet
                  </span>
                }
              >
                {recentCustomers.map((customer) => (
                  <CustomerItem
                    key={`recent-${customer.id}`}
                    customer={customer}
                    isSelected={customer.id === value}
                    onSelect={handleSelect}
                    testid={`${testid}-recent-${customer.id}`}
                  />
                ))}
              </CommandGroup>
            )}
            {groupedCustomers.map(([letter, list]) => (
              <CommandGroup key={letter} heading={letter}>
                {list.map((customer) => (
                  <CustomerItem
                    key={customer.id}
                    customer={customer}
                    isSelected={customer.id === value}
                    onSelect={handleSelect}
                    testid={`${testid}-item-${customer.id}`}
                  />
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
